import { useState } from 'react';
import { Outlet, Link, Navigate, useLocation } from 'react-router-dom';
import { HelpCircle, Menu, X } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { NotificationDropdown } from '../components/notifications';

export default function DashboardLayout() {
  const { user, seller, isAuthenticated, isLoading, logout } = useAuthStore();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const navigation = [
    { name: 'Overview', href: '/dashboard', icon: 'home' },
    { name: 'Orders', href: '/dashboard/orders', icon: 'package' },
    { name: 'Purchases', href: '/dashboard/purchases', icon: 'shopping-bag' },
    { name: 'Settings', href: '/dashboard/settings', icon: 'settings' },
  ];

  const sellerNav = [
    { name: 'Seller Dashboard', href: '/dashboard/seller', icon: 'store' },
    { name: 'Products', href: '/dashboard/seller/products', icon: 'box' },
    { name: 'Analytics', href: '/dashboard/seller/analytics', icon: 'bar-chart' },
    { name: 'Payouts', href: '/dashboard/seller/payouts', icon: 'dollar-sign' },
  ];

  return (
    <div className="min-h-screen flex bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-leather border-r border-gold/10 flex flex-col transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="p-6 border-b border-gold/10 flex items-center justify-between">
          <Link to="/" className="font-western text-xl text-gold">
            OutlawOasis
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-text-secondary hover:text-cream"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          <div className="mb-4">
            <h3 className="px-3 text-xs font-semibold text-text-muted uppercase tracking-wider">
              Account
            </h3>
            <div className="mt-2 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    location.pathname === item.href
                      ? 'bg-gold/10 text-gold'
                      : 'text-text-secondary hover:bg-gold/5 hover:text-cream'
                  }`}
                >
                  <span>{item.name}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* Seller Navigation */}
          {(user?.role === 'seller' || user?.role === 'admin' || user?.role === 'owner') && seller && (
            <div className="mb-4">
              <h3 className="px-3 text-xs font-semibold text-text-muted uppercase tracking-wider">
                Seller
              </h3>
              <div className="mt-2 space-y-1">
                {sellerNav.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      location.pathname === item.href
                        ? 'bg-gold/10 text-gold'
                        : 'text-text-secondary hover:bg-gold/5 hover:text-cream'
                    }`}
                  >
                    <span>{item.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Admin Link */}
          {(user?.role === 'admin' || user?.role === 'owner') && (
            <div>
              <h3 className="px-3 text-xs font-semibold text-text-muted uppercase tracking-wider">
                Admin
              </h3>
              <div className="mt-2">
                <Link
                  to="/admin"
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg text-text-secondary hover:bg-gold/5 hover:text-cream transition-colors"
                >
                  <span>Admin Panel</span>
                </Link>
              </div>
            </div>
          )}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-gold/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-surface border border-gold/30 flex items-center justify-center">
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-gold font-semibold">
                  {user?.username?.[0]?.toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <p className="font-medium text-cream">{user?.displayName || user?.username}</p>
              <p className="text-xs text-text-muted capitalize">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={() => logout()}
            className="w-full text-left px-3 py-2 text-sm text-text-secondary hover:text-cream transition-colors"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:pl-64">
        {/* Top Header Bar */}
        <header className="flex-shrink-0 h-16 bg-leather border-b border-gold/10 flex items-center justify-between px-4 lg:px-8 gap-2">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-text-secondary hover:text-cream"
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="flex items-center gap-2 ml-auto">
            <Link
              to="/support"
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-text-secondary hover:text-cream transition-colors rounded-lg hover:bg-surface"
            >
              <HelpCircle className="w-5 h-5" />
              <span className="hidden sm:inline">Support</span>
            </Link>
            <NotificationDropdown />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
